import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { CHAT } from "@/constants/testIds";

/**
 * Lightweight scripted assistant (no LLM). Pattern-matches keywords and
 * returns canned answers sourced from the actual MonetiseUP FAQ/content.
 */
const KB = [
  {
    match: ["get started", "start", "onboarding", "join", "signup", "sign up"],
    reply:
      "Easy! Email contact@monetiseup.com and our team will walk you through onboarding. Setup typically takes 24–48 hours.",
  },
  {
    match: ["ecpm", "rate", "earning", "revenue"],
    reply:
      "Average eCPM is around $4.50, but many publishers earn higher based on traffic quality, geo, and vertical. Want a custom estimate? Email contact@monetiseup.com.",
  },
  {
    match: ["payment", "payout", "pay", "net 30", "threshold"],
    reply:
      "We pay NET 60, monthly. Minimum payout threshold is $100. Detailed revenue reports are included.",
  },
  {
    match: ["integrate", "integration", "header bidding", "sdk"],
    reply:
      "We support header bidding, direct integration, and programmatic connections. Our tech team handles implementation, testing, and optimization.",
  },
  {
    match: ["analytics", "report", "dashboard"],
    reply:
      "Yes — real-time analytics on impressions, clicks, revenue, eCPM, and fill rates inside the publisher dashboard at dashboard.monetiseup.com.",
  },
  {
    match: ["ad format", "format", "video", "display", "mobile", "native"],
    reply:
      "We support display banners, native, video (pre-roll/mid-roll/outstream), mobile, and rich media. Your account manager will recommend the best mix.",
  },
  {
    match: ["manga"],
    reply:
      "Our manga-optimized solutions live at manga.monetiseup.com — reader-first placements, genre targeting, and mobile-first creative.",
  },
  {
    match: ["brand safety", "fraud", "safety"],
    reply:
      "Comprehensive brand safety controls, category blocking, advertiser filters, and active fraud detection are included on every plan.",
  },
  {
    match: ["advertiser", "campaign", "budget"],
    reply:
      "We work with advertisers of all sizes. Tell us your goals, target audience, and budget at contact@monetiseup.com and we'll craft a strategy.",
  },
  {
    match: ["targeting", "audience"],
    reply:
      "Demographic, geographic, behavioral, contextual, interest-based, plus lookalike and custom segments.",
  },
  {
    match: ["account manager", "support", "help"],
    reply:
      "Every publisher gets a dedicated account manager plus 24/7 technical support. Or visit /help-center.",
  },
];

const SUGGESTIONS = [
  "How do I get started?",
  "What eCPM can I expect?",
  "What ad formats do you support?",
  "Tell me about manga monetisation",
];

function findReply(text) {
  const t = text.toLowerCase();
  const hit = KB.find((e) => e.match.some((m) => t.includes(m)));
  if (hit) return hit.reply;
  return "Great question! Drop us a line at contact@monetiseup.com and a specialist will get back to you within one business day.";
}

export default function AiChatbot() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    {
      from: "bot",
      text:
        "Hi! I'm Bluey, the MonetiseUP assistant. Ask about onboarding, eCPMs, payments, formats, or manga monetisation.",
    },
  ]);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = (text) => {
    const value = (text ?? input).trim();
    if (!value) return;
    setMessages((m) => [...m, { from: "user", text: value }]);
    setInput("");
    setTimeout(() => {
      setMessages((m) => [...m, { from: "bot", text: findReply(value) }]);
    }, 500);
  };

  return (
    <>
      <motion.button
        data-testid={CHAT.bubble}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 left-6 z-[10000] w-14 h-14 rounded-full bg-gradient-to-br from-[#0047FF] to-[#00F0FF] grid place-items-center shadow-[0_10px_40px_rgba(0,71,255,0.5)] pulse-glow"
        aria-label="Open chat assistant"
      >
        {open ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={CHAT.panel}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-24 left-6 z-[10000] w-[92vw] sm:w-[400px] glass-strong rounded-3xl overflow-hidden flex flex-col"
            style={{ maxHeight: "70vh" }}
          >
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#0047FF] to-[#00F0FF] grid place-items-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="font-medium text-sm">Bluey</div>
                  <div className="text-[10px] text-emerald-400 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Online · MonetiseUP Support
                  </div>
                </div>
              </div>
              <button
                data-testid={CHAT.close}
                onClick={() => setOpen(false)}
                aria-label="Close chat"
                className="text-white/60 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div
              ref={scrollerRef}
              className="flex-1 overflow-y-auto p-5 space-y-3"
              style={{ minHeight: 280 }}
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      m.from === "user"
                        ? "bg-gradient-to-br from-[#0047FF] to-[#00F0FF] text-white"
                        : "bg-white/5 border border-white/10 text-white/85"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              {messages.length === 1 && (
                <div className="pt-3">
                  <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">
                    Quick prompts
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        onClick={() => send(s)}
                        className="text-xs px-3 py-1.5 rounded-full border border-white/10 hover:border-[#00F0FF]/50 hover:bg-white/5 transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="p-3 border-t border-white/10 flex gap-2"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask anything..."
                data-testid={CHAT.input}
                className="flex-1 px-4 py-2.5 rounded-full bg-white/5 border border-white/10 focus:border-[#00F0FF]/50 outline-none text-sm"
              />
              <button
                type="submit"
                data-testid={CHAT.send}
                className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0047FF] to-[#00F0FF] grid place-items-center"
                aria-label="Send message"
              >
                <Send className="w-4 h-4 text-white" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
